import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent } from '@/components/ui/card'
import Editor from '@monaco-editor/react'
import { Play } from 'lucide-react'
import { request } from '@/lib/request'

interface CodeEditorProps {
  value?: string
  defaultValue?: string
  language?: string
  isTest?: boolean
  onChange?: (value: string) => void
}

export function CodeEditor({
  value,
  defaultValue = '',
  language = 'python',
  isTest = false,
  onChange,
}: CodeEditorProps) {
  const [code, setCode] = useState(value || defaultValue)
  const [logs, setLogs] = useState('')
  const [isError, setIsError] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (value !== undefined) {
      setCode(value)
    }
  }, [value])

  const handleCodeChange = (newValue: string | undefined) => {
    const val = newValue || ''
    setCode(val)
    onChange?.(val)
  }

  const handleRun = async () => {
    setLoading(true)
    try {
      const resp = await request('/system/exec', {
        data: { code },
      })
      if (resp && resp.code > -1) {
        setLogs(resp.data)
        setIsError(false)
      } else {
        setLogs(resp?.msg || '执行失败')
        setIsError(true)
      }
    } catch (error) {
      setLogs(String(error))
      setIsError(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <Editor
          height="40vh"
          language={language}
          value={code}
          onChange={handleCodeChange}
          theme="vs-dark"
          options={{
            minimap: { enabled: false },
            fontSize: 16,
            scrollBeyondLastLine: false,
            automaticLayout: true,
            scrollbar: {
              verticalScrollbarSize: 8,
              horizontalScrollbarSize: 8,
            },
          }}
        />

        {isTest && (
          <div className="flex justify-end">
            <Button onClick={handleRun} disabled={loading}>
              <Play className="mr-2 h-4 w-4" />
              {loading ? '执行中...' : 'Test'}
            </Button>
          </div>
        )}

        {logs && (
          <Alert variant={isError ? 'destructive' : 'default'}>
            <AlertDescription>
              <pre className="whitespace-pre-wrap font-mono text-sm">
                {logs.replace(/\\n/g, '\n')}
              </pre>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}
